import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import Select from 'components/Select';
import { getLocaleMap } from 'utils/locales';
import MKInput from 'components/MaterialKit/MKInput';
import { useNavigate, useParams } from 'react-router-dom';

const EditLayoutForm = ({ layout, headers, footers, menus, onSubmit, onCancel, ...props }) => {
  const [lm, setLm] = useState({});
  const { appId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getLocaleMap(['errors_required', 'cancel_button', 'save_button'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const initialValues = useMemo(() => ({
    name: layout?.name || '',
    header: layout?.header?.section_id || null,
    footer: layout?.footer?.section_id || null,
    menu: layout?.menu?.menu_id || null,
  }), [layout]);

  const headersOptions = useMemo(() => (headers || []).sort(
    (sd1, sd2) => (sd1?.name || '').localeCompare(sd2?.name),
  ).map((header) => ({
    label: header?.name,
    value: header.section_id,
  })), [headers]);

  const footerOptions = useMemo(() => (footers || []).sort(
    (sd1, sd2) => (sd1?.name || '').localeCompare(sd2?.name),
  ).map((footer) => ({
    label: footer?.name,
    value: footer.section_id,
  })), [footers]);

  const menuOptions = useMemo(() => (menus || []).sort(
    (sd1, sd2) => (sd1?.display_name || '').localeCompare(sd2?.display_name),
  ).map((menu) => ({
    label: menu?.display_name,
    value: menu?.menu_id,
  })), [menus]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, setFieldValue, handleSubmit, errors, values, isSubmitting, dirty, touched }) => {
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item xs={12}>
                <MKInput
                  name="name"
                  label="Name"
                  value={values.name || ''}
                  onChange={handleChange('name')}
                  onBlur={handleBlur('name')}
                  error={touched.name && !!errors.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Select
                  label="Header"
                  value={values.header}
                  onChange={(v) => setFieldValue('header', v)}
                  options={headersOptions}
                  invalid={touched.header && !!errors.header}
                />
              </Grid>
              <Grid item xs={12}>
                <Select
                  label="Footer"
                  value={values.footer}
                  onChange={(v) => setFieldValue('footer', v)}
                  options={footerOptions}
                  invalid={touched.footer && !!errors.footer}
                />
              </Grid>
              <Grid item xs={12}>
                <Select
                  label="Menu"
                  value={values.menu}
                  onChange={(v) => setFieldValue('menu', v)}
                  options={menuOptions}
                  invalid={touched.menu && !!errors.menu}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-start" mt={2}>
              <Grid item xs={12} md={6} xl={4}>
                <MKBox display="flex">
                  <MKBox display="flex" flex={1}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={!values.header}
                      onClick={() => { navigate(`/section-attributes/${values.header}`); }}
                    >
                      Edit Header
                    </Button>
                  </MKBox>
                  <MKBox display="flex" flex={1} ml={1}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={!values.footer}
                      onClick={() => { navigate(`/section-attributes/${values.footer}`); }}
                    >
                      Edit Footer
                    </Button>
                  </MKBox>
                  <MKBox display="flex" flex={1} ml={1}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={!values.menu}
                      onClick={() => { navigate(`/app/${appId}/menu/${values.menu}`); }}
                    >
                      Edit Menu
                    </Button>
                  </MKBox>
                </MKBox>
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} md={6} xl={4}>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
                <MKBox display="flex">
                  <MKBox display="flex" flex={1}>
                    <Button
                      onClick={onCancel}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                    >
                      {lm.cancel_button}
                    </Button>
                  </MKBox>
                  <MKBox display="flex" flex={1} ml={2}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={isSubmitting || !dirty}
                    >
                      {isSubmitting ? (
                        <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                      ) : lm.save_button }
                    </Button>
                  </MKBox>
                </MKBox>
              </Grid>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditLayoutForm.propTypes = {
  layout: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  headers: PropTypes.array,
  footers: PropTypes.array,
  menus: PropTypes.array,
};

EditLayoutForm.defaultProps = {
  onSubmit: () => {},
  onCancel: () => {},
};

export default EditLayoutForm;
